import { DiagnosticReport } from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import {
  SearchFieldDefinition,
  ResourceSearchConfig,
  FhirSearchParams,
} from '@/components/resource-search/ResourceSearchForm.types';
import {
  ALL_REPORT_CATEGORY_OPTIONS,
  REPORT_RESULT_OPTIONS,
  ReportCategoryValue,
  ReportResultValue,
} from './utils/report-files.consts';
import { REPORT_CATEGORY_TO_DIAGNOSTIC_CATEGORY } from './utils/report-files.utils';
import { ARKON } from '@/consts/systems.consts';

export interface ReportSearchParams {
  category?: ReportCategoryValue;
  code?: string;
  codeDisplay?: string;
  result?: ReportResultValue;
  dateFrom?: Date | null;
  dateTo?: Date | null;
}

export const REPORT_SEARCH_DEFAULTS: ReportSearchParams = {
  category: undefined,
  code: undefined,
  codeDisplay: undefined,
  result: undefined,
  dateFrom: null,
  dateTo: null,
};

export const reportSearchConfig: ResourceSearchConfig<
  ReportSearchParams,
  DiagnosticReport
> = {
  resourceType: 'DiagnosticReport',
  itemsPerPage: 20,
  buildSearchQuery: (params, page, itemsPerPage) => {
    const query: FhirSearchParams = {
      _count: itemsPerPage,
      _offset: (page - 1) * itemsPerPage,
      _sort: '-date',
    };

    if (params.category) {
      const coding = REPORT_CATEGORY_TO_DIAGNOSTIC_CATEGORY[params.category];
      query.category = `${coding.system}|${coding.code}`;
    }

    if (params.code) {
      // Use system|code when category dictates a known system; otherwise fallback to text search
      if (params.category === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC) {
        query.code = `${ARKON.CS.GENETIC_TESTS.BASE}|${params.code}`;
      } else if (
        params.category === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION
      ) {
        query.code = `${ARKON.CS.REPORTS.INVESTIGATION_TYPE.BASE}|${params.code}`;
      } else if (
        params.category === ARKON.CS.REPORTS.CATEGORY.CODES.PRENATAL_CHECK
      ) {
        query.code = `${ARKON.CS.PREBIRTH_CHECKS.BASE}|${params.code}`;
      } else {
        query['code:text'] = params.code;
      }
    }

    if (params.result) {
      const coding = REPORT_RESULT_OPTIONS.find(
        (opt) => opt.value === params.result
      )?.coding;
      if (coding?.code && coding?.system) {
        query['conclusion'] = `${coding.system}|${coding.code}`;
      }
    }

    if (params.dateFrom && params.dateTo) {
      const from = params.dateFrom.toISOString().split('T')[0];
      const to = params.dateTo.toISOString().split('T')[0];
      query._filter = `(date ge ${from} and date le ${to})`;
    } else if (params.dateFrom) {
      query.date = `ge${params.dateFrom.toISOString().split('T')[0]}`;
    } else if (params.dateTo) {
      query.date = `le${params.dateTo.toISOString().split('T')[0]}`;
    }

    return query;
  },
};

interface ReportFieldOptions {
  t: TFunction<'patient-pages' | 'lims' | 'common'>;
  searchParams: ReportSearchParams;
  onSearch: (updates: Partial<ReportSearchParams>) => void;
}

export function getReportSearchFields({
  t,
  searchParams,
  onSearch,
}: ReportFieldOptions): SearchFieldDefinition<ReportSearchParams>[] {
  return [
    {
      key: 'category',
      type: 'select',
      placeholder: t('reportTable.categoryPlaceholder', {
        ns: 'patient-pages',
        defaultValue: 'Κατηγορία',
      }),
      options: ALL_REPORT_CATEGORY_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`reportCards.categoryOptions.${option.label}`, {
          ns: 'patient-pages',
          defaultValue: option.label,
        }),
      })),
      value: searchParams.category,
      onChange: (value: unknown) =>
        onSearch({ category: (value as ReportCategoryValue) || undefined }),
    },
    {
      key: 'code',
      type: 'codeableConcept',
      placeholder: t('reportTable.codePlaceholder', {
        ns: 'patient-pages',
        defaultValue: 'Κωδικός / Τύπος',
      }),
      binding:
        searchParams.category === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC
          ? ARKON.VS.GENETIC_TESTS_EL
          : searchParams.category ===
              ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION
            ? ARKON.VS.REPORT_FILE_TYPE_EL
            : searchParams.category ===
                ARKON.CS.REPORTS.CATEGORY.CODES.PRENATAL_CHECK
              ? ARKON.VS.PREBIRTH_CHECKS_EL
              : undefined,
      show: (params: ReportSearchParams) => Boolean(params.category),
      defaultValue: searchParams.code
        ? {
            coding: [
              {
                code: searchParams.code,
                display: searchParams.codeDisplay,
              },
            ],
          }
        : undefined,
      onChange: (value: unknown) => {
        const coding = (
          value as { coding?: { code?: string; display?: string }[] }
        )?.coding?.[0];
        onSearch({
          code: coding?.code || undefined,
          codeDisplay: coding?.display || undefined,
        });
      },
    },
    {
      key: 'result',
      type: 'select',
      placeholder: t('reportTable.resultPlaceholder', {
        ns: 'patient-pages',
        defaultValue: 'Αποτέλεσμα',
      }),
      options: REPORT_RESULT_OPTIONS.map((option) => ({
        value: option.value,
        label: t(`reportCards.resultOptions.${option.label}`, {
          ns: 'patient-pages',
          defaultValue: option.label,
        }),
      })),
      value: searchParams.result,
      onChange: (value: unknown) =>
        onSearch({ result: (value as ReportResultValue) || undefined }),
    },
    {
      key: 'dateFrom',
      type: 'date',
      placeholder: t('reportTable.fromDatePlaceholder', {
        ns: 'patient-pages',
        defaultValue: 'Από ημερομηνία',
      }),
      value: searchParams.dateFrom || null,
      onChange: (value: Date | null) => onSearch({ dateFrom: value }),
    },
    {
      key: 'dateTo',
      type: 'date',
      placeholder: t('reportTable.toDatePlaceholder', {
        ns: 'patient-pages',
        defaultValue: 'Έως ημερομηνία',
      }),
      value: searchParams.dateTo || null,
      onChange: (value: Date | null) => onSearch({ dateTo: value }),
    },
  ].filter(Boolean) as SearchFieldDefinition<ReportSearchParams>[];
}
